import { useMemo } from "react";
import { shallow } from "zustand/shallow";
import { useCurrentPublicShow } from "hooks";
import useDateFnsLocale from "hooks/useDateFnsLocale";
import useUnresolvedVariableLabel from "hooks/useUnresolvedVariableLabel";
import useShowCustomFields from "hooks/useShowCustomFields";
import useCurrentExhibitorPairId from "hooks/useCurrentExhibitorPairId";
import { useCurrentExhibitor } from "store/current-exhibitor/current-exhibitor.store";
import getTextWithVariables from "tools/task/getTextWithVariables";

/**
 * A Show Info section's description as its reader sees it on the current Show.
 *
 * Custom-field variables resolve as they do for a task: keyed by the field's
 * English title, valued from the reader's own stand. The pair is only read
 * here, never fetched — the exhibitor pages that render a manual have it, and
 * an organiser has none, so their tokens read as unresolved. `stand` is left
 * out, so stand variables — which a manual never offers — stay unresolved.
 */
const useTermDescriptionWithVariables = (description: string): string => {
  const [show] = useCurrentPublicShow();
  const locale = useDateFnsLocale();
  const unresolvedLabel = useUnresolvedVariableLabel();
  const [customFields] = useShowCustomFields({ localised: true });
  const exhibitorPairId = useCurrentExhibitorPairId();
  const { pair } = useCurrentExhibitor(
    (state) => state.getExhibitorPair(show?.id ?? "", exhibitorPairId),
    shallow
  );
  const standToCustomFields = pair?.stand?.standToCustomFields;

  return useMemo(
    () =>
      getTextWithVariables({
        show,
        customFields,
        ...(standToCustomFields && { task: { standToCustomFields } }),
        description,
        locale,
        unresolvedLabel,
      }),
    [
      show,
      customFields,
      standToCustomFields,
      description,
      locale,
      unresolvedLabel,
    ]
  );
};

export default useTermDescriptionWithVariables;
